/**
 * Caster Mold Color Constants
 * Domain colors for the Caster Mold module (component identification, 3D model)
 *
 * NOTE: UI colors (tooltips, overlays, gradients) come from the shared EAF theme.
 * Access them via Viewer3DColors, Viewer3DSettings, etc.
 *
 * Hierarchy (positions are relative to parent):
 *   Mold direct children (pos 1-13):
 *     1: Copper Mold Tube, 2: Mold Body, 3: Oil Distribution Gasket,
 *     4: Level Control, 5: Oil Connector, 6: Water Jacket Flume,
 *     7: Flanges (group), 8: Collars (group), 9: Seals (group),
 *     10-13: Foot Roll Assemblies (Left/Right/Internal/External)
 *
 *   Flanges sub-positions (1-5): Upper, Low Upper, Lower, Closing, WJ Fixing
 *   Collars sub-positions (1-2): Upper, Lower
 *   Seals sub-positions (1-6): Upper, Copper Tube, Water Jacket,
 *     Lower Internal, Lower External, Oil Connector O-Ring
 *   Foot Roll sub-positions (1-7): Foot Roll, Roll Shaft, Roller Support,
 *     Shaft Keeper, Z1 Spray, Spray Nozzle (+ assembly itself at parent level)
 */

import {
  Viewer3DColors,
  Viewer3DSettings,
  Viewer3DEmissiveColors,
  Viewer3DMaterials,
  Viewer3DGeometry,
  StatusIndicatorColors,
} from "../../../shared/theme";

// Re-export shared theme constants for convenience
export {
  Viewer3DColors,
  Viewer3DSettings,
  Viewer3DEmissiveColors,
  Viewer3DMaterials,
  Viewer3DGeometry,
  StatusIndicatorColors,
};

// =============================================================================
// EMPTY / DEFAULT COLORS
// =============================================================================

/** Gray color for empty positions (no part installed) */
export const EMPTY_POSITION_COLOR = "#9E9E9E";

// =============================================================================
// DOMAIN COLORS - Physical positions (should NOT change with theme)
// =============================================================================

/**
 * Standalone component colors (Mold direct children, positions 1-6)
 *
 * Industrial/mechanical palette:
 * - Copper Mold Tube: copper/orange (matches GLB model)
 * - Mold Body: steel blue (structural housing)
 * - Oil/fluid components: warm industrial tones
 * - Level Control: signal green (instrumentation)
 */
const MoldComponentColors = {
  COPPER_MOLD_TUBE: "#CD7F32", // Copper/bronze — the signature component
  MOLD_BODY: "#546E7A", // Blue Gray 600 — steel housing
  OIL_DISTRIBUTION_GASKET: "#8D6E63", // Brown 400 — oil system component
  LEVEL_CONTROL: "#66BB6A", // Green 400 — instrumentation/sensor
  OIL_CONNECTOR: "#FFB74D", // Orange 300 — oil line connector
  WATER_JACKET_FLUME: "#4FC3F7", // Light Blue 300 — water cooling

  EMPTY: EMPTY_POSITION_COLOR, // Gray for empty/uninstalled
} as const;

/**
 * Flange sub-position colors (under Flanges group, positions 1-5)
 *
 * Warm metallic palette — flanges are structural ring-shaped parts
 */
const FlangeColors = {
  UPPER_FLANGE: "#EF5350", // Red 400 — top flange
  LOW_UPPER_FLANGE: "#AB47BC", // Purple 400 — intermediate
  LOWER_FLANGE: "#FFA726", // Orange 400 — bottom flange
  CLOSING_FLANGE: "#FFCA28", // Amber 400 — closing ring
  WATER_JACKET_FIXING_FLANGE: "#26C6DA", // Cyan 400 — water jacket mount
} as const;

/**
 * Collar sub-position colors (under Collars group, positions 1-2)
 *
 * Contrasting pair for the two collar positions
 */
const CollarColors = {
  UPPER_COLLAR: "#7E57C2", // Deep Purple 400
  LOWER_COLLAR: "#42A5F5", // Blue 400
} as const;

/**
 * Seal sub-position colors (under Seals group, positions 1-8)
 *
 * Cool-tone palette — seals are consumable gasket/ring components
 * NOTE: Copper Tube Seal and Oil Connector O-Ring each have two physical
 * meshes in the GLB (upper/lower), sharing the same color per type.
 */
const SealColors = {
  UPPER_SEAL: "#26A69A", // Teal 400
  COPPER_TUBE_SEAL: "#EC407A", // Pink 400 — shared by upper & lower meshes
  WATER_JACKET_SEAL: "#5C6BC0", // Indigo 400
  LOWER_INTERNAL_SEAL: "#9CCC65", // Light Green 400
  LOWER_EXTERNAL_SEAL: "#FF7043", // Deep Orange 400
  OIL_CONNECTOR_ORING: "#BDBDBD", // Gray 400 — shared by upper & lower meshes
} as const;

/**
 * Foot Roll Assembly sub-position colors (positions 1-6 under each assembly)
 *
 * Same colors for sub-components across all 4 assemblies (Left/Right/Internal/External).
 * The assembly itself is identified by its parent position (10-13).
 *
 * Industrial/mechanical palette — rotating roller components
 */
const FootRollSubColors = {
  FOOT_ROLL: "#78909C", // Blue Gray 400 — the roller itself
  ROLL_SHAFT: "#37474F", // Blue Gray 800 — central shaft (dark steel)
  ROLLER_SUPPORT: "#8D6E63", // Brown 400 — bearing support
  SHAFT_KEEPER: "#A1887F", // Brown 300 — retention clip
  Z1_SPRAY: "#29B6F6", // Light Blue 400 — spray bar
  SPRAY_NOZZLE: "#4DD0E1", // Cyan 300 — nozzle tip
} as const;

/**
 * Foot Roll Assembly parent colors (Mold positions 10-13)
 *
 * Each assembly gets a distinct color when viewed as a unit
 */
export const FootRollAssemblyColors: Record<number, string> = {
  10: "#5D4037", // Brown 800 — Left
  11: "#1565C0", // Blue 800 — Right
  12: "#2E7D32", // Green 800 — Internal
  13: "#6A1B9A", // Purple 800 — External
};

/**
 * Group container colors (Mold positions 7-9)
 * These are non-replaceable assembly nodes in the tree
 */
const GroupColors: Record<number, string> = {
  7: "#D32F2F", // Red 700 — Flanges group
  8: "#7B1FA2", // Purple 700 — Collars group
  9: "#00897B", // Teal 600 — Seals group
};

// =============================================================================
// STATUS COLORS (reuses shared StatusIndicatorColors)
// =============================================================================

/**
 * Status-based colors for wear indicators
 * OK uses the component's default color (undefined = no override)
 */
const STATUS_COLORS = {
  OK: undefined, // Use default component color
  WARNING: StatusIndicatorColors.WARNING, // Orange
  ALERT: StatusIndicatorColors.ALERT, // Red
  BLANK: EMPTY_POSITION_COLOR, // Gray
} as const;

// =============================================================================
// COLOR LOOKUP MAPS - O(1) lookups by position ID
// =============================================================================

/**
 * Mold direct children: position ID → color
 * Positions 1-6: standalone components
 * Positions 7-9: group containers
 * Positions 10-13: foot roll assemblies
 */
const MOLD_POSITION_COLORS: Record<number, string> = {
  // Standalone components
  1: MoldComponentColors.COPPER_MOLD_TUBE,
  2: MoldComponentColors.MOLD_BODY,
  3: MoldComponentColors.OIL_DISTRIBUTION_GASKET,
  4: MoldComponentColors.LEVEL_CONTROL,
  5: MoldComponentColors.OIL_CONNECTOR,
  6: MoldComponentColors.WATER_JACKET_FLUME,

  // Group containers
  7: GroupColors[7],
  8: GroupColors[8],
  9: GroupColors[9],

  // Foot Roll Assemblies
  10: FootRollAssemblyColors[10],
  11: FootRollAssemblyColors[11],
  12: FootRollAssemblyColors[12],
  13: FootRollAssemblyColors[13],
};

/**
 * Flanges group: sub-position ID → color
 */
const FLANGE_POSITION_COLORS: Record<number, string> = {
  1: FlangeColors.UPPER_FLANGE,
  2: FlangeColors.LOW_UPPER_FLANGE,
  3: FlangeColors.LOWER_FLANGE,
  4: FlangeColors.CLOSING_FLANGE,
  5: FlangeColors.WATER_JACKET_FIXING_FLANGE,
};

/**
 * Collars group: sub-position ID → color
 */
const COLLAR_POSITION_COLORS: Record<number, string> = {
  1: CollarColors.UPPER_COLLAR,
  2: CollarColors.LOWER_COLLAR,
};

/**
 * Seals group: sub-position ID → color
 * Aligned with GLB mesh mapping (constants.ts): 8 sub-positions
 */
const SEAL_POSITION_COLORS: Record<number, string> = {
  1: SealColors.UPPER_SEAL,
  2: SealColors.COPPER_TUBE_SEAL, // Upper mesh
  3: SealColors.COPPER_TUBE_SEAL, // Lower mesh (same component type)
  4: SealColors.WATER_JACKET_SEAL,
  5: SealColors.LOWER_INTERNAL_SEAL,
  6: SealColors.LOWER_EXTERNAL_SEAL,
  7: SealColors.OIL_CONNECTOR_ORING, // Upper mesh
  8: SealColors.OIL_CONNECTOR_ORING, // Lower mesh (same component type)
};

/**
 * Foot Roll Assembly: sub-position ID → color (shared across all 4 assemblies)
 * Aligned with GLB mesh mapping (constants.ts): 7 sub-positions
 */
const FOOT_ROLL_SUB_COLORS: Record<number, string> = {
  1: FootRollSubColors.FOOT_ROLL,
  2: FootRollSubColors.ROLL_SHAFT,
  3: FootRollSubColors.ROLLER_SUPPORT,
  4: FootRollSubColors.SHAFT_KEEPER,
  5: FootRollSubColors.Z1_SPRAY,
  6: FootRollSubColors.SPRAY_NOZZLE, // 1st mesh
  7: FootRollSubColors.SPRAY_NOZZLE, // 2nd mesh (same component type)
};

/**
 * Group parent position IDs on the Mold (7=Flanges, 8=Collars, 9=Seals, 10-13=FootRoll)
 * Maps each group position to its sub-position color lookup
 */
const GROUP_SUB_COLOR_MAP: Record<number, Record<number, string>> = {
  7: FLANGE_POSITION_COLORS,
  8: COLLAR_POSITION_COLORS,
  9: SEAL_POSITION_COLORS,
  10: FOOT_ROLL_SUB_COLORS,
  11: FOOT_ROLL_SUB_COLORS,
  12: FOOT_ROLL_SUB_COLORS,
  13: FOOT_ROLL_SUB_COLORS,
};

// =============================================================================
// COLOR GETTER FUNCTIONS
// =============================================================================

/**
 * Get the color for any Caster Mold component by position.
 *
 * For top-level Mold children (positions 1-13), pass only `positionId`.
 * For sub-components inside a group (Flanges/Collars/Seals/FootRoll),
 * pass the parent's `positionId` and the child's `subPositionId`.
 *
 * @param positionId - Position on the Mold (1-13)
 * @param subPositionId - Sub-position within a group (optional)
 * @returns Hex color string
 *
 * @example
 * getComponentColor(1)     // Copper Mold Tube → "#CD7F32"
 * getComponentColor(7, 1)  // Flanges > Upper Flange → "#EF5350"
 * getComponentColor(10, 2) // Foot Roll Left > Roll Shaft → "#37474F"
 * getComponentColor(99)    // Unknown → EMPTY_POSITION_COLOR
 */
export const getComponentColor = (
  positionId: number,
  subPositionId?: number,
): string => {
  // If sub-position requested, look up in the appropriate group map
  if (subPositionId !== undefined) {
    const subMap = GROUP_SUB_COLOR_MAP[positionId];
    if (subMap) {
      return subMap[subPositionId] ?? EMPTY_POSITION_COLOR;
    }
    // positionId is not a group — fall through to top-level lookup
  }

  return MOLD_POSITION_COLORS[positionId] ?? EMPTY_POSITION_COLOR;
};
